import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";

export function SchedulingPage() {
  const [efsms, setEfsms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  useEffect(() => {
    axios
      .get(`${API}/site-efsms/`)
      .then((res) => {
        setEfsms(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const monthData = MONTH_NAMES.map((name, i) => {
    const m = i + 1;

    // Monthly shows every month for any site that has monthly EFSMs
    const monthlySites = new Set(
      efsms.filter((e) => e.is_monthly).map((e) => e.site),
    );

    // Bi-annual: sites where starting month matches OR starting month + 6 matches
    const biannualSites = new Set(
      efsms
        .filter((e) => {
          if (!e.is_bi_annual || !e.bi_annual_scheduled_month) return false;
          const start = e.bi_annual_scheduled_month;
          const second = start + 6 > 12 ? start + 6 - 12 : start + 6;
          return m === start || m === second;
        })
        .map((e) => e.site),
    );
    const annualSites = new Set(
      efsms
        .filter((e) => e.is_annual && e.annual_scheduled_month === m)
        .map((e) => e.site),
    );

    const allSiteIds = new Set([
      ...monthlySites,
      ...biannualSites,
      ...annualSites,
    ]);

    return {
      month: m,
      name,
      monthlySites,
      biannualSites,
      annualSites,
      totalSites: allSiteIds.size,
    };
  });

  const selectedData = selectedMonth ? monthData[selectedMonth - 1] : null;

  const getSitesForMonth = (data) => {
    if (!data) return [];
    const siteMap = {};
    efsms
      .filter((e) => e.is_monthly)
      .forEach((e) => {
        if (!siteMap[e.site])
          siteMap[e.site] = {
            siteId: e.site,
            siteName: e.site_name,
            siteAddress: e.site_address,
            accountName: e.account_name,
            types: [],
          };
        if (!siteMap[e.site].types.includes("Monthly"))
          siteMap[e.site].types.push("Monthly");
      });
    efsms
      .filter((e) => {
        if (!e.is_bi_annual || !e.bi_annual_scheduled_month) return false;
        const start = e.bi_annual_scheduled_month;
        const second = start + 6 > 12 ? start + 6 - 12 : start + 6;
        return data.month === start || data.month === second;
      })
      .forEach((e) => {
        if (!siteMap[e.site])
          siteMap[e.site] = {
            siteId: e.site,
            siteName: e.site_name,
            siteAddress: e.site_address,
            accountName: e.account_name,
            types: [],
          };
        if (!siteMap[e.site].types.includes("Bi-Annual"))
          siteMap[e.site].types.push("Bi-Annual");
      });
    efsms
      .filter((e) => e.is_annual && e.annual_scheduled_month === data.month)
      .forEach((e) => {
        if (!siteMap[e.site])
          siteMap[e.site] = {
            siteId: e.site,
            siteName: e.site_name,
            siteAddress: e.site_address,
            accountName: e.account_name,
            types: [],
          };
        if (!siteMap[e.site].types.includes("Annual"))
          siteMap[e.site].types.push("Annual");
      });
    return Object.values(siteMap);
  };

  const typeColor = (t) =>
    t === "Monthly"
      ? colors.blue
      : t === "Bi-Annual"
        ? colors.orange
        : colors.green;

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Scheduling</div>
        <div style={{ fontSize: "13px", color: "#999" }}>{currentYear}</div>
      </div>
      <div style={styles.content}>
        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "12px",
                marginBottom: "24px",
              }}
            >
              {monthData.map((md) => {
                const isCurrentMonth = md.month === currentMonth;
                const isPast = md.month < currentMonth;
                const isSelected = selectedMonth === md.month;
                return (
                  <div
                    key={md.month}
                    onClick={() =>
                      setSelectedMonth(isSelected ? null : md.month)
                    }
                    style={{
                      backgroundColor: isSelected
                        ? colors.darkGrey
                        : colors.white,
                      borderRadius: "10px",
                      padding: "16px",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                      border: `2px solid ${isCurrentMonth ? colors.blue : isSelected ? colors.darkGrey : colors.border}`,
                      opacity: isPast ? 0.7 : 1,
                      transition: "all 0.2s",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "700",
                          fontSize: "15px",
                          color: isSelected
                            ? colors.white
                            : isCurrentMonth
                              ? colors.blue
                              : colors.text,
                        }}
                      >
                        {md.name}
                      </div>
                      {isCurrentMonth && (
                        <span
                          style={{
                            fontSize: "10px",
                            backgroundColor: colors.blue,
                            color: "white",
                            padding: "2px 6px",
                            borderRadius: "8px",
                            fontWeight: "600",
                          }}
                        >
                          THIS MONTH
                        </span>
                      )}
                    </div>
                    <div
                      style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                    >
                      {md.monthlySites.size > 0 && (
                        <span
                          style={{
                            fontSize: "11px",
                            backgroundColor: isSelected
                              ? "rgba(255,255,255,0.2)"
                              : "#e8f4fd",
                            color: isSelected ? "white" : colors.blue,
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontWeight: "600",
                          }}
                        >
                          {md.monthlySites.size} Monthly
                        </span>
                      )}
                      {md.biannualSites.size > 0 && (
                        <span
                          style={{
                            fontSize: "11px",
                            backgroundColor: isSelected
                              ? "rgba(255,255,255,0.2)"
                              : "#fef3e8",
                            color: isSelected ? "white" : colors.orange,
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontWeight: "600",
                          }}
                        >
                          {md.biannualSites.size} Bi-Annual
                        </span>
                      )}
                      {md.annualSites.size > 0 && (
                        <span
                          style={{
                            fontSize: "11px",
                            backgroundColor: isSelected
                              ? "rgba(255,255,255,0.2)"
                              : "#eafaf1",
                            color: isSelected ? "white" : colors.green,
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontWeight: "600",
                          }}
                        >
                          {md.annualSites.size} Annual
                        </span>
                      )}

                      {md.totalSites === 0 && (
                        <span
                          style={{
                            fontSize: "11px",
                            color: isSelected
                              ? "rgba(255,255,255,0.5)"
                              : "#ccc",
                          }}
                        >
                          No services
                        </span>
                      )}
                    </div>
                    {md.totalSites > 0 && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "12px",
                          color: isSelected ? "rgba(255,255,255,0.7)" : "#999",
                        }}
                      >
                        {md.totalSites} site{md.totalSites !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedData && (
              <div style={styles.card}>
                <div
                  style={{
                    backgroundColor: colors.darkGrey,
                    color: colors.white,
                    padding: "14px 20px",
                    borderRadius: "6px 6px 0 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontWeight: "700", fontSize: "16px" }}>
                    {selectedData.name} {currentYear}
                  </div>
                  <div style={{ fontSize: "13px", opacity: 0.7 }}>
                    {selectedData.totalSites} site
                    {selectedData.totalSites !== 1 ? "s" : ""} scheduled
                  </div>
                </div>
                <div
                  style={{
                    ...styles.tableHeader,
                    gridTemplateColumns: "2fr 2fr 1fr 1fr",
                    borderRadius: "0",
                  }}
                >
                  <div>Site</div>
                  <div>Account</div>
                  <div>Address</div>
                  <div>Service Types</div>
                </div>
                {getSitesForMonth(selectedData).length === 0 ? (
                  <div style={styles.loading}>
                    No sites scheduled for this month.
                  </div>
                ) : (
                  getSitesForMonth(selectedData).map((site, idx) => (
                    <div
                      key={site.siteId}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 2fr 1fr 1fr",
                        padding: "14px 20px",
                        borderBottom: `1px solid ${colors.border}`,
                        alignItems: "center",
                        backgroundColor:
                          idx % 2 === 0 ? colors.white : "#fafafa",
                      }}
                    >
                      <div style={{ fontWeight: "500", color: colors.text }}>
                        {site.siteName}
                      </div>
                      <div style={{ fontSize: "13px", color: "#666" }}>
                        {site.accountName}
                      </div>
                      <div style={{ fontSize: "12px", color: "#999" }}>
                        {site.siteAddress}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          flexWrap: "wrap",
                        }}
                      >
                        {site.types.map((t) => (
                          <span
                            key={t}
                            style={{
                              fontSize: "11px",
                              backgroundColor: typeColor(t),
                              color: "white",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontWeight: "600",
                            }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                )}
                <div style={styles.count}>
                  {getSitesForMonth(selectedData).length} site
                  {getSitesForMonth(selectedData).length !== 1
                    ? "s"
                    : ""} in {selectedData.name}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
